import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TimeEntriesService } from '../../shared/services/time-entries.service';
import { getCurrentUser } from 'aws-amplify/auth';
import { TaskService } from '../../shared/services/task.service';
import { ProjectService } from '../../shared/services/project.service';
import { UserService } from '../../shared/services/user.service';
import { ClientService } from '../../shared/services/client.service';
import { TenantService } from '../../shared/services/tenant.service';
import { PosthogService } from '../../shared/services/posthog.service';
import { User } from '../../shared/services/user';
import { Task } from '../../shared/models/task.model';
import { Subscription } from 'rxjs';
import { User as UserModel } from '../../shared/models/user.model';

interface TaskWithHierarchy {
  id: string;
  nameTask: string;
  limitDateToDo: string | null;
  status: string | null;
  priority: string | null;
  projectName: string;
  phaseName: string;
  projectId: string;
  phaseId: string;
  validatedBy: string | null;
  validatedAt: string | null;
}

interface TodoWithClient {
  id: string;
  name: string;
  description: string | null;
  status: string | null;
  priority: string | null;
  dueDate: string | null;
  clientName: string;
  tenantName: string;
  clientId: string | null;
  tenantId: string | null;
  projectId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  todoLinkId: string;
  validatedBy: string | null;
  validatedAt: string | null;
}

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './dashboard.html'
})
export class Dashboard implements OnInit, OnDestroy {
  private taskService = inject(TaskService);
  private timeEntriesService = inject(TimeEntriesService);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  private clientService = inject(ClientService);
  private tenantService = inject(TenantService);
  private posthogService = inject(PosthogService);
  user = inject(User);
  private router = inject(Router);

  userTasks = signal<TaskWithHierarchy[]>([]);
  userTodos = signal<TodoWithClient[]>([]);
  loading = signal<boolean>(true);
  loadingTodos = signal<boolean>(true);
  error = signal<string | null>(null);
  errorTodos = signal<string | null>(null);
  hasCRMAccess = signal<boolean>(false);
  showDoneProjects = signal<boolean>(false);
  showDoneTodos = signal<boolean>(false);
  disappearingItems = signal<Set<string>>(new Set());

  isItemDisappearing(id: string): boolean {
    return this.disappearingItems().has(id);
  }

  /** Flip a project task's status locally — immutable so the signal notifies. */
  private patchTaskStatus(id: string, status: string | null) {
    this.userTasks.update(tasks => tasks.map(t => (t.id === id ? { ...t, status } : t)));
  }

  /** Flip a to-do's status locally — immutable so the signal notifies. */
  private patchTodoStatus(id: string, status: string | null) {
    this.userTodos.update(todos => todos.map(td => (td.id === id ? { ...td, status } : td)));
  }

  private setDisappearing(id: string, on: boolean) {
    this.disappearingItems.update(set => {
      const next = new Set(set);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
  }

  /** Today's date — for the header. Set once per session. */
  readonly today = new Date();

  /** Time-of-day greeting for the header. */
  greeting = computed(() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  });

  /** Current user's first name (falls back to full name) for the greeting. */
  get firstName(): string {
    const info = this.user.userInfo();
    return info.first_name || info.name || '';
  }

  /**
   * At-a-glance KPI counts over the currently-visible open items (tasks + todos).
   * Recomputes automatically when the task/todo signals change.
   */
  stats = computed(() => {
    let todo = 0, inProgress = 0, overdue = 0, dueToday = 0;
    for (const t of this.userTasks()) {
      if (t.status === 'DONE') continue;
      if (t.status === 'INPROGRESS') inProgress++; else todo++;
      if (this.isOverdue(t.limitDateToDo)) overdue++;
      if (this.isToday(t.limitDateToDo)) dueToday++;
    }
    for (const td of this.userTodos()) {
      if (td.status === 'DONE') continue;
      if (td.status === 'IN_PROGRESS') inProgress++; else todo++;
      if (this.isTodoOverdue(td.dueDate)) overdue++;
      if (this.isToday(td.dueDate)) dueToday++;
    }
    return { todo, inProgress, overdue, dueToday };
  });

  // ── Reworked dashboard data ──────────────────────────────────────────────
  hoursThisWeek = signal<number>(0);
  hoursLoading = signal<boolean>(true);
  projectProgress = signal<{ id: string; name: string; subtitle: string; encoded: number; estimate: number; pct: number; color: string }[]>([]);
  progressLoading = signal<boolean>(true);
  private encodedByProject: Record<string, number> = {};
  private progressLoadedIds = new Set<string>();
  private hoursLoaded = false;
  private cognitoId: string | null = null;

  private static readonly PROGRESS_COLORS = ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4'];

  /** Number of accessible projects (KPI card). */
  get activeProjectsCount(): number {
    return this.currentProjects.length;
  }

  /** Unified "my tasks" list: assigned project tasks + assigned to-dos. */
  myTasks = computed(() => {
    const list: {
      id: string; title: string; subtitle: string; status: string | null;
      priority: string | null; dueDate: string | null; kind: 'task' | 'todo'; projectId?: string;
    }[] = [];
    for (const t of this.userTasks()) {
      list.push({ id: t.id, title: t.nameTask, subtitle: t.projectName, status: t.status, priority: t.priority, dueDate: t.limitDateToDo, kind: 'task', projectId: t.projectId });
    }
    for (const td of this.userTodos()) {
      list.push({ id: td.id, title: td.name, subtitle: td.clientName || td.tenantName || '', status: td.status, priority: td.priority, dueDate: td.dueDate, kind: 'todo' });
    }
    return list;
  });

  /** Open a "my tasks" item — project task or to-do — from the dashboard. */
  openItem(item: { kind: 'task' | 'todo'; id: string; projectId?: string }) {
    if (item.kind === 'task' && item.projectId) {
      this.router.navigate(['/projects', item.projectId], { queryParams: { taskId: item.id, view: 'details' } });
    } else {
      this.router.navigate(['/tasks'], { queryParams: { todoId: item.id } });
    }
  }

  /** Nearest upcoming deadlines from open tasks + to-dos. */
  upcomingDeadlines = computed(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const items: { id: string; date: string; title: string; subtitle: string; projectId?: string }[] = [];
    for (const t of this.userTasks()) {
      if (t.status === 'DONE' || !t.limitDateToDo) continue;
      items.push({ id: t.id, date: t.limitDateToDo, title: t.nameTask, subtitle: t.projectName, projectId: t.projectId });
    }
    for (const td of this.userTodos()) {
      if (td.status === 'DONE' || !td.dueDate) continue;
      items.push({ id: td.id, date: td.dueDate, title: td.name, subtitle: td.clientName || td.tenantName || '' });
    }
    return items
      .filter(i => new Date(i.date) >= startOfToday)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 5);
  });

  /** Total hours logged by the current user since the start of this week. */
  private async loadHoursThisWeek() {
    if (!this.cognitoId) return;
    try {
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      monday.setHours(0, 0, 0, 0);
      const entries = await this.timeEntriesService.fetchAllEntries({
        employeeId: this.cognitoId, // time entries store the Cognito id, not the DB user id
        startDate: monday.toISOString(),
        endDate: now.toISOString(),
      });
      const hours = entries.reduce(
        (acc, e) => acc + (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 3_600_000,
        0,
      );
      this.hoursThisWeek.set(hours);
    } catch (e) {
      console.error('[Dashboard] Failed to load hours this week', e);
    } finally {
      this.hoursLoading.set(false);
    }
  }

  /** Encoded-hours-vs-estimate progress for the top projects (My projects card). */
  private async loadProjectProgress() {
    const projects = this.currentProjects.slice(0, 6);
    await Promise.all(
      projects.map(async (p: any) => {
        if (!p.projectId || this.progressLoadedIds.has(p.projectId)) return;
        this.progressLoadedIds.add(p.projectId);
        try {
          const entries = await this.timeEntriesService.fetchAllByProjectId(p.projectId, p.projectName);
          this.encodedByProject[p.projectId] = entries.reduce(
            (a, e) => a + (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 3_600_000,
            0,
          );
        } catch {
          this.encodedByProject[p.projectId] = 0;
        }
      }),
    );
    this.projectProgress.set(
      projects.map((p: any, i: number) => {
        const estimate = p.estimatedHours || 0;
        const encoded = this.encodedByProject[p.projectId] ?? 0;
        const pct = estimate > 0 ? Math.min(Math.round((encoded / estimate) * 100), 100) : 0;
        return {
          id: p.projectId,
          name: p.projectName,
          subtitle: p.client?.name || p.tenant?.name || '',
          encoded,
          estimate,
          pct,
          color: Dashboard.PROGRESS_COLORS[i % Dashboard.PROGRESS_COLORS.length],
        };
      }),
    );
    this.progressLoading.set(false);
  }

  // Tenant filter
  tenants: { id: string; name: string }[] = [];
  selectedProjectTenantId = '';
  selectedTodoTenantId = '';
  private userTenantIds: string[] = [];

  /**
   * Derived rather than stored: `user.admin()` is a signal that is set by an
   * async init, so a stored flag computed inside the tenants$ callback could be
   * decided before the role was known and then never revisited — the switch
   * simply never appeared. Reading it on each change detection cannot go stale.
   */
  get showTenantFilter(): boolean {
    if (!this.tenants.length) return false;
    return this.user.admin() || this.userTenantIds.length > 1;
  }

  private subscriptions: Subscription[] = [];
  private currentUserId: string | null = null;
  private todosLoaded = false;
  private initialTasksLoaded = false;
  private initialTodosLoaded = false;
  private knownClients: { id: string; name: string; tenantId?: string }[] = [];

  // Store current data
  private currentTasks: Task[] = [];
  private cachedFilteredTasks: Task[] = [];
  private currentProjectStages: any[] = [];
  private currentProjects: any[] = [];
  private fetchDebounceTimer: any = null;
  private employees: UserModel[] = [];

  async ngOnInit() {
    try {
      // Get current user ID
      const authUser = await getCurrentUser();
      const cognitoId = authUser.userId;
      this.cognitoId = cognitoId; // time entries are keyed by the Cognito id

      // Subscribe to users$ to get the database user ID
      this.subscriptions.push(
        this.clientService.clients$.subscribe(clients => {
          this.knownClients = clients.map(c => ({ id: c.id, name: c.name, tenantId: (c as any).tenantId }));
        })
      );

      // Subscribe to users for name resolution (validatedBy)
      this.subscriptions.push(
        this.userService.users$.subscribe(users => {
          this.employees = users;
        })
      );

      // Subscribe to tenants for filter dropdown + name resolution
      this.subscriptions.push(
        this.tenantService.tenants$.subscribe(tenants => {
          // Always store tenants for name resolution in todos
          this.tenants = tenants;
        })
      );
      await this.tenantService.init();

      this.subscriptions.push(
        this.userService.users$.subscribe(users => {
          const user = users.find(u => u.cognitoId === cognitoId);
          if (user?.id) {
            const userIdChanged = this.currentUserId !== user.id;
            this.currentUserId = user.id;
            console.log('📥 [Dashboard] Current user ID:', this.currentUserId);

            // Setup tenant filter for non-admin users
            if (!this.user.admin()) {
              const tenantIds = user?.tenants?.map((t: any) => t.tenantId) ?? [];
              this.userTenantIds = tenantIds;
              if (tenantIds.length > 1 && user?.tenants) {
                // Merge user tenants into this.tenants if not already present
                const userTenants = user.tenants
                  .filter((ut: any) => ut.tenant)
                  .map((ut: any) => ut.tenant as { id: string; name: string });
                const existingIds = new Set(this.tenants.map(t => t.id));
                for (const ut of userTenants) {
                  if (!existingIds.has(ut.id)) this.tenants.push(ut);
                }
              }
            }

            this.fetchProjectTasks();

            // Load week hours once the user id is known.
            if (!this.hoursLoaded) {
              this.hoursLoaded = true;
              this.loadHoursThisWeek();
            }
            this.loadProjectProgress();

            // Only load todos once when user ID is first available
            if (userIdChanged && !this.todosLoaded) {
              this.loadUserTodos();
              this.todosLoaded = true;
            }
          }
        })
      );

      // Subscribe to projectsStage$ for hierarchy lookup — re-fetch when data arrives
      this.subscriptions.push(
        this.projectService.projectsStage$.subscribe(stages => {
          this.currentProjectStages = stages;
          if (this.currentUserId) this.fetchProjectTasks();
        })
      );

      // Subscribe to projects$ for hierarchy lookup — only update when no tenant filter is active
      this.subscriptions.push(
        this.projectService.projects$.subscribe(projects => {
          if (!this.selectedProjectTenantId) {
            this.currentProjects = projects;
          }
          if (this.currentUserId) {
            this.fetchProjectTasks();
            this.loadProjectProgress();
          }
        })
      );

      // Subscribe to tasks$ for real-time updates (e.g. when a task status changes)
      this.subscriptions.push(
        this.taskService.tasks$.subscribe(() => {
          if (this.currentUserId && this.initialTasksLoaded) {
            this.rebuildTaskHierarchy();
          }
        })
      );

      this.loading.set(false);
    } catch (e) {
      console.error('❌ [Dashboard] Failed to initialize:', e);
      this.error.set('Failed to load dashboard');
      this.loading.set(false);
    }

    // Check CRM feature flag access
    this.checkCRMAccess();

    // Listen for feature flag updates
    this.posthogService.onFeatureFlags(() => {
      this.checkCRMAccess();
    });
  }

  private checkCRMAccess(): void {
    this.hasCRMAccess.set(this.posthogService.featureFlagCRM());
    console.log('[Dashboard] CRM access (todos widget):', this.hasCRMAccess());
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private async loadUserTodos() {
    if (!this.currentUserId) return;
    try {
      // Only show loading spinner on initial load
      if (!this.initialTodosLoaded) {
        this.loadingTodos.set(true);
      }
      const todoUserLinks = await this.clientService.getTodosByUserFiltered(
        this.currentUserId,
        {
          excludeDone: !this.showDoneTodos(),
          tenantId: this.selectedTodoTenantId || undefined
        }
      );

      if (todoUserLinks.length === 0) {
        this.userTodos.set([]);
        this.loadingTodos.set(false);
        this.initialTodosLoaded = true;
        return;
      }

      const todosWithClients: TodoWithClient[] = todoUserLinks
        .filter(link => !!link.todo)
        .map(link => {
          const todo = link.todo;
          const clientId = (todo as any).clientId;
          const tenantId = (todo as any).tenantId;
          const projectId = (todo as any).projectId;

          // Resolve tenant name
          let tenantName = '';
          if (tenantId) {
            tenantName = this.tenants.find(t => t.id === tenantId)?.name ?? '';
          }

          // Resolve client name: direct clientId > project's clientId
          let clientName = '';
          if (clientId) {
            clientName = this.knownClients.find(c => c.id === clientId)?.name ?? '';
          } else if (projectId) {
            // Try to resolve client from the project
            const project = this.currentProjects.find((p: any) => p.projectId === projectId);
            if (project?.clientId) {
              clientName = this.knownClients.find(c => c.id === project.clientId)?.name ?? '';
            }
          }

          return {
            id: todo.id,
            name: todo.name,
            description: todo.description,
            status: todo.status,
            priority: todo.priority,
            dueDate: todo.dueDate,
            clientName,
            tenantName,
            clientId: clientId || null,
            tenantId: tenantId || null,
            projectId: projectId || null,
            createdAt: todo.createdAt,
            updatedAt: (todo as any).updatedAt || todo.createdAt,
            todoLinkId: link.id,
            validatedBy: (todo as any).validatedBy ?? null,
            validatedAt: (todo as any).validatedAt ?? null,
          } as TodoWithClient;
        });

      // Sort by updatedAt DESC — modified/new tasks float to the top
      todosWithClients.sort((a, b) => {
        const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return db - da;
      });

      this.userTodos.set(todosWithClients);
      this.loadingTodos.set(false);
      this.initialTodosLoaded = true;
    } catch (e) {
      console.error('[Dashboard] Error loading todos:', e);
      this.errorTodos.set('Failed to load todos');
      this.loadingTodos.set(false);
    }
  }

  /** Fetch tasks from server with filter, then build hierarchy for display. */
  private fetchProjectTasks() {
    // Debounce to coalesce rapid calls (e.g. from setTenantFilter triggering both observer + list)
    clearTimeout(this.fetchDebounceTimer);
    this.fetchDebounceTimer = setTimeout(() => this._doFetchProjectTasks(), 200);
  }

  private async _doFetchProjectTasks() {
    if (!this.currentUserId) {
      console.log('⏳ [Dashboard] Waiting for user ID...');
      return;
    }

    try {
      // Only show loading spinner on initial load
      if (!this.initialTasksLoaded) {
        this.loading.set(true);
      }
      // Server-side filter: exclude DONE unless toggle is on
      const tasks = await this.taskService.listTasksFiltered({
        excludeDone: !this.showDoneProjects()
      });

      // Cache fetched tasks for real-time rebuilds
      this.cachedFilteredTasks = tasks;

      console.log(`📊 [Dashboard] Fetched ${tasks.length} tasks (excludeDone=${!this.showDoneProjects()})`);

      this.buildAndSetTaskHierarchy(tasks);
      this.loading.set(false);
      this.initialTasksLoaded = true;
    } catch (e) {
      console.error('❌ [Dashboard] Error fetching tasks:', e);
      this.loading.set(false);
    }
  }

  /** Rebuild task hierarchy from cached data + live task status from TaskService.
   *  Called when tasks$ emits (real-time update) to avoid a full server re-fetch. */
  private rebuildTaskHierarchy() {
    // Merge latest task data from the real-time tasks$ stream
    const liveTasks = this.taskService.getTasksSnapshot();

    if (!this.cachedFilteredTasks.length && !liveTasks.length) return;

    // Start from cached tasks and merge updates
    const mergedById = new Map<string, Task>();

    // Add all cached tasks first
    for (const cached of this.cachedFilteredTasks) {
      mergedById.set(cached.id!, cached);
    }

    // Merge / add live tasks (includes newly assigned tasks not in cache)
    for (const live of liveTasks) {
      if ((live as any).isDeleted) continue;
      const existing = mergedById.get(live.id!);
      if (existing) {
        mergedById.set(live.id!, { ...existing, ...live });
      } else {
        // New task not in cache — include it (could have just been assigned to user)
        mergedById.set(live.id!, live);
      }
    }

    let merged = Array.from(mergedById.values());

    // If showDoneProjects is off, filter out tasks that are DONE — except the one
    // the user just checked, which stays in place until its exit animation ends.
    if (!this.showDoneProjects()) {
      const animating = this.disappearingItems();
      merged = merged.filter(t => t.status !== 'DONE' || animating.has(t.id!));
    }

    this.buildAndSetTaskHierarchy(merged as Task[]);
  }

  /** Shared logic to filter, map, sort and set the userTasks signal. */
  private buildAndSetTaskHierarchy(tasks: Task[]) {
    // Filter tasks assigned to current user
    const userAssignedTasks = tasks.filter((task: Task) =>
      task.employeeId &&
      Array.isArray(task.employeeId) &&
      task.employeeId.includes(this.currentUserId!)
    );

    console.log(`✅ [Dashboard] Found ${userAssignedTasks.length} tasks for user (from ${tasks.length} total, stages=${this.currentProjectStages.length}, projects=${this.currentProjects.length})`);

    // Build lookup maps for O(1) access
    const stageMap = new Map(this.currentProjectStages.map((s: any) => [s.projectStageId, s]));
    const projectMap = new Map(this.currentProjects.map((p: any) => [p.projectId, p]));

    // Transform tasks with hierarchy data
    const tasksWithHierarchy = userAssignedTasks
      .map((task: Task) => {
        const stage = stageMap.get(task.projectStageId);
        if (!stage) return null; // Stage not loaded yet or deleted

        const project = projectMap.get(stage.projectId);
        if (!project) return null; // Project was deleted — task is orphaned, hide it

        return {
          id: task.id!,
          nameTask: task.nameTask,
          limitDateToDo: task.limitDateToDo ?? null,
          status: task.status ?? null,
          priority: task.priority ?? null,
          projectName: project.projectName,
          phaseName: stage.stageName,
          projectId: project.projectId!,
          phaseId: stage.projectStageId!,
          validatedBy: task.validatedBy ?? null,
          validatedAt: task.validatedAt ?? null,
        } as TaskWithHierarchy;
      })
      .filter((task): task is TaskWithHierarchy => task !== null);

    // Sort by due date
    tasksWithHierarchy.sort((a, b) => {
      if (!a.limitDateToDo && !b.limitDateToDo) return 0;
      if (!a.limitDateToDo) return 1;
      if (!b.limitDateToDo) return -1;
      return new Date(a.limitDateToDo).getTime() - new Date(b.limitDateToDo).getTime();
    });

    this.userTasks.set(tasksWithHierarchy);
    console.log(`✨ [Dashboard] Displaying ${tasksWithHierarchy.length} tasks`);
  }

  // Keep this method for manual refresh button
  async listUserTasks() {
    console.log('🔄 [Dashboard] Manual refresh triggered');
    this.fetchProjectTasks();
  }

  async refreshTodos() {
    console.log('🔄 [Dashboard] Manual todos refresh triggered');
    await this.loadUserTodos();
  }

  toggleShowDoneProjects() {
    this.showDoneProjects.update(v => !v);
    this.fetchProjectTasks(); // re-fetch from server with new filter
  }

  toggleShowDoneTodos() {
    this.showDoneTodos.update(v => !v);
    this.loadUserTodos(); // re-fetch from server with new filter
  }

  /**
   * The dashboard-wide tenant switch. Both sections follow one control: an admin
   * reading "the dashboard for tenant X" expects the projects, the tasks and the
   * deadlines to agree, not two independent filters.
   */
  async onTenantFilterChange(tenantId: string): Promise<void> {
    this.selectedProjectTenantId = tenantId;
    this.selectedTodoTenantId = tenantId;
    this.onTodoTenantFilterChange();
    await this.onProjectTenantFilterChange();
    // currentProjects has just been replaced, so the progress card must be rebuilt
    // from the new set rather than keeping the previous tenant's rows.
    this.progressLoading.set(true);
    await this.loadProjectProgress();
  }

  async onProjectTenantFilterChange(): Promise<void> {
    console.log('🏢 [Dashboard] Project tenant filter changed:', this.selectedProjectTenantId || 'All');
    // Server-side: one-off query that does NOT touch the shared ProjectService state
    if (this.selectedProjectTenantId) {
      this.currentProjects = await this.projectService.listProjectsFiltered({
        tenantIds: [this.selectedProjectTenantId]
      });
    } else {
      // Fetch ALL projects (paginated) instead of relying on the potentially-incomplete snapshot
      this.currentProjects = await this.projectService.listProjectsFiltered();
    }
    this.rebuildTaskHierarchy();
  }

  onTodoTenantFilterChange(): void {
    console.log('🏢 [Dashboard] Todo tenant filter changed:', this.selectedTodoTenantId || 'All');
    this.loadUserTodos();
  }

  getStatusBadgeClass(status: string | null): string {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'INPROGRESS':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'NOTBEGIN':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  }

  getPriorityBadgeClass(priority: string | null): string {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'LOW':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  formatDateTime(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatRelativeTime(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin}min`;
    if (diffH < 24) return `il y a ${diffH}h`;
    if (diffDays === 1) return 'hier';
    if (diffDays < 7) return `il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  formatTodoDate(dateString: string | null): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  getValidatedByName(userId: string | null): string {
    if (!userId) return '';
    const emp = this.employees.find(e => e.id === userId);
    if (emp) return `${emp.firstName ?? ''} ${emp.name ?? ''}`.trim();
    return 'Unknown';
  }

  isOverdue(dateString: string | null): boolean {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  }

  /** True when the given date falls on today (used by the "Due today" KPI). */
  isToday(dateString: string | null): boolean {
    if (!dateString) return false;
    const d = new Date(dateString);
    const now = new Date();
    return d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
  }

  navigateToTask(task: TaskWithHierarchy): void {
    // Navigate to the project with the task ID and view query parameters to open task detail modal directly
    this.router.navigate(['/projects', task.projectId], {
      queryParams: { taskId: task.id, view: 'details' }
    });
  }

  navigateToTodo(todo: TodoWithClient) {
    this.router.navigate(['/tasks'], {
      queryParams: { todoId: todo.id }
    });
  }

  async toggleTaskStatus(item: { kind: 'task' | 'todo'; id: string; status: string | null }, event: Event) {
    event.stopPropagation(); // Prevent card click navigation
    if (item.kind === 'todo') {
      const todo = this.userTodos().find(td => td.id === item.id);
      if (todo) await this.toggleTodoStatus(todo, event);
      return;
    }

    const task = this.userTasks().find(tk => tk.id === item.id);
    if (!task) return;

    const originalStatus = task.status;
    const newStatus = originalStatus === 'DONE' ? 'INPROGRESS' : 'DONE';
    const shouldDisappear = newStatus === 'DONE' && !this.showDoneProjects();

    // Optimistic: checkbox, strike-through and KPI counts update on this frame.
    this.patchTaskStatus(task.id, newStatus);
    if (shouldDisappear) {
      // Stagger sequence: wait 300ms for checkbox pop/bounce animation to complete, then trigger exit animation
      setTimeout(() => {
        this.setDisappearing(task.id, true);
        
        // Wait for the exit animation (600ms) and then immediately remove from UI list optimistically
        setTimeout(() => {
          this.setDisappearing(task.id, false);
          this.userTasks.update(tasks => tasks.filter(t => t.id !== task.id));
        }, 600);
      }, 300);
    }

    // Fire & forget the backend update in the background
    this.taskService.updateTask(task.id, { status: newStatus }).catch(e => {
      console.error('[Dashboard] Backend failed to toggle task status:', e);
      // If it fails, we DO NOT revert it locally in this session to keep the UI smooth and avoid flickering.
      // Re-opening or refreshing the page will naturally fetch the unresolved state from the backend and display it again.
    });
  }

  async toggleTodoStatus(todo: TodoWithClient, event: Event) {
    event.stopPropagation(); // Prevent card click navigation
    const originalStatus = todo.status;
    const newStatus = originalStatus === 'DONE' ? 'TODO' : 'DONE';
    const shouldDisappear = newStatus === 'DONE' && !this.showDoneTodos();

    // Optimistic: patch the signal (not just the object) so the row re-renders now.
    this.patchTodoStatus(todo.id, newStatus);
    if (shouldDisappear) {
      // Stagger sequence: wait 300ms for checkbox pop/bounce animation to complete, then trigger exit animation
      setTimeout(() => {
        this.setDisappearing(todo.id, true);
        
        // Wait for the exit animation (600ms) and then immediately remove from UI list optimistically
        setTimeout(() => {
          this.setDisappearing(todo.id, false);
          this.userTodos.update(todos => todos.filter(t => t.id !== todo.id));
        }, 600);
      }, 300);
    }

    // Fire & forget the backend update in the background
    this.clientService.updateTodo(todo.id, { status: newStatus }).catch(e => {
      console.error('[Dashboard] Backend failed to toggle todo status:', e);
      // If it fails, we DO NOT revert it locally in this session to keep the UI smooth.
      // It will naturally reappear on the next load of the page.
    });
  }

  getTodoStatusBadgeClass(status: string | null): string {
    switch (status) {
      case 'DONE':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'TODO':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  }

  getTodoStatusLabel(status: string | null): string {
    switch (status) {
      case 'DONE':
        return '✓';
      case 'IN_PROGRESS':
        return '→';
      case 'TODO':
        return '○';
      default:
        return '○';
    }
  }

  getTodoPriorityBadgeClass(priority: string | null): string {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'LOW':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  }

  getTodoPriorityIcon(priority: string | null): string {
    switch (priority) {
      case 'HIGH':
        return '↑';
      case 'MEDIUM':
        return '•';
      case 'LOW':
        return '↓';
      default:
        return '•';
    }
  }

  isTodoOverdue(dueDate: string | null): boolean {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  }
}
